// Shared country/currency lookup for the plan & topup pricing screens
// (PlanPricingEditor, PlanCountryPricing, TopupCountryPricing).
//
// Country -> currency used to be resolved by matching react-select-country-list's
// display label against currency-codes' official ISO-4217 country name string
// (e.g. "United Arab Emirates" vs "United Arab Emirates (The)"). Those two
// npm packages use different naming conventions for the same country, so the
// match silently failed for ~1 in 6 countries, including the UAE, US and UK.
//
// This resolves currency by ISO alpha-2 code instead (already returned by
// react-select-country-list as `value`, previously discarded), cross-referenced
// against world-countries' `cca2 -> currencies` map. Two ISO codes always match
// exactly, so there is no string-matching ambiguity left.
import countryList from "react-select-country-list";
import worldCountries from "world-countries";
import { DODO_SUPPORTED_CURRENCIES, isDodoSupportedCurrency } from "../constants/dodoCurrencies";

// [{ value: "AE", label: "United Arab Emirates" }, ...] — both the ISO code and
// the display label are kept; callers must not discard `value`.
export const getCountryOptions = () => countryList().getData();

const CCA2_TO_CURRENCY = worldCountries.reduce((acc, entry) => {
  const cca2 = String(entry?.cca2 || "").toUpperCase();
  const currencyCode = entry?.currencies ? Object.keys(entry.currencies)[0] : "";
  if (cca2 && currencyCode) {
    acc[cca2] = String(currencyCode).toUpperCase();
  }
  return acc;
}, {});

// Resolves a currency from an ISO alpha-2 country code. Returns "" when the
// country has no assigned currency in world-countries (e.g. Antarctica) — the
// caller should fall back to manual currency selection in that case, not treat
// it as an error.
export const getCurrencyForCountryCode = (isoCode) => {
  const key = String(isoCode || "").trim().toUpperCase();
  if (!key) return "";
  return CCA2_TO_CURRENCY[key] || "";
};

// Resolves a currency for a country option ({ value, label }) as returned by
// getCountryOptions()/an Autocomplete selection.
export const getCurrencyForCountryOption = (option) =>
  getCurrencyForCountryCode(option?.value);

// Dodo-supported currencies, sorted, for the manual-selection fallback.
export const getDodoCurrencyOptions = () =>
  Array.from(DODO_SUPPORTED_CURRENCIES).sort();

export { isDodoSupportedCurrency };

export const normalizeCountryLabel = (value) => String(value || "").trim().toLowerCase();
